import { useEffect, useState } from "react";
import { Modal } from "../../ui/modal";
import Alert from "../../ui/alert/Alert";
import Spinner from "../../ui/Spinner/Spinner";
import CopyButton from "../../ui/button/copyButton";
import { fetchWallets } from "../../wallet/API/services";
import {
  buildPayUrl,
  createWalletLink,
  getWalletLinkFee,
} from "../API/services";
import { CreatedWalletLink } from "../types";
import CurrencyFlag from "../CurrencyFlag";
import { useCurrencyStore } from "../../../stores/useCurrencyStore";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface WalletOption {
  currency: string;
  name: string;
  flag?: string;
}

const FEE_DEBOUNCE_MS = 500;

// Value for <input type="datetime-local"> in the browser's local time.
const toLocalInput = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const CreateWalletLinkModal = ({ isOpen, onClose, onCreated }: Props) => {
  const [wallets, setWallets] = useState<WalletOption[]>([]);
  const [loadingWallets, setLoadingWallets] = useState(false);
  const [currency, setCurrency] = useState("");
  const [iso2, setIso2] = useState("");
  const [amount, setAmount] = useState("");
  const [payerName, setPayerName] = useState("");
  const [expiresAt, setExpiresAt] = useState(""); // datetime-local value
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState<CreatedWalletLink | null>(null);

  // Fee state: null means "not available" (not fetched yet or failed)
  const [fee, setFee] = useState<number | null>(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeError, setFeeError] = useState(false);

  const {
    walletSupportedCurrencies,
    hasWalletSupportedLoaded,
    getWalletSupportedCurrencies,
  } = useCurrencyStore();

  // ---- Resolve iso2 from the store using the wallet's currency as the code
  const currencyData = walletSupportedCurrencies.find(
    (c) => c.code === currency
  );

  const supportingCountries: any[] = currencyData?.hasSupportingCountries
    ? Object.values(currencyData.supportingCountries || {}).filter(
        (c: any) => c.allowWalletCreation === true
      )
    : [];

  const needsCountry = supportingCountries.length > 0;

  // One country (e.g. XAF) is preselected; several (e.g. XOF) need a choice.
  const resolvedIso2 = needsCountry
    ? supportingCountries.length === 1
      ? supportingCountries[0].iso2
      : iso2
    : currencyData?.iso2 || "";

  const amountValue = Number(amount);
  const hasValidAmount = Number.isInteger(amountValue) && amountValue > 0;

  // Reset every time the modal opens, and make sure currency data is loaded
  useEffect(() => {
    if (!isOpen) return;
    setCurrency("");
    setIso2("");
    setAmount("");
    setPayerName("");
    setExpiresAt(toLocalInput(new Date(Date.now() + 7 * 86400 * 1000)));
    setDescription("");
    setError("");
    setCreated(null);
    setFee(null);
    setFeeError(false);
    setFeeLoading(false);

    // No-op if already loaded (guarded inside the store)
    getWalletSupportedCurrencies();

    let cancelled = false;
    setLoadingWallets(true);
    fetchWallets()
      .then((data: any[]) => {
        if (cancelled) return;
        setWallets(
          (data || []).map((w) => ({
            currency: w.currency,
            name: w.name,
            flag: w.flag,
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setError("Could not load your wallets.");
      })
      .finally(() => {
        if (!cancelled) setLoadingWallets(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // ---- Fetch the fee after the user enters an amount (debounced)
  useEffect(() => {
    if (!isOpen) return;

    // Nothing to fetch yet
    if (!hasValidAmount || !resolvedIso2) {
      setFee(null);
      setFeeError(false);
      setFeeLoading(false);
      return;
    }

    let cancelled = false;
    setFeeLoading(true);
    setFeeError(false);

    const timer = setTimeout(async () => {
      try {
        const f = await getWalletLinkFee(amountValue, resolvedIso2);
        if (cancelled) return;
        setFee(f);
        setFeeError(false);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load fee:", err);
        setFee(null);
        setFeeError(true);
      } finally {
        if (!cancelled) setFeeLoading(false);
      }
    }, FEE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isOpen, amountValue, hasValidAmount, resolvedIso2]);

  // True from the moment an amount is entered until the fee request settles,
  // including the debounce window before the request starts.
  const feePending =
    hasValidAmount && !!resolvedIso2 && (feeLoading || (fee === null && !feeError));

  const handleSubmit = async () => {
    if (feePending) return;
    if (!currency) return setError("Select the wallet to be funded");
    if (!expiresAt) return setError("Choose an expiry date and time");
    const expiryDate = new Date(expiresAt);
    if (Number.isNaN(expiryDate.getTime()) || expiryDate.getTime() <= Date.now())
      return setError("Expiry must be in the future");
    if (!hasWalletSupportedLoaded)
      return setError("Currency details are still loading. Please try again.");
    if (needsCountry && !resolvedIso2)
      return setError("Select a country for this wallet");
    if (!resolvedIso2)
      return setError("Could not determine the country for this currency");
    if (!hasValidAmount)
      return setError("Enter a whole amount greater than 0");

    try {
      setSubmitting(true);
      setError("");
      const link = await createWalletLink({
        amount: amountValue,
        currency,
        iso2: resolvedIso2,
        expiresAt: expiryDate.toISOString(),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(payerName.trim() ? { payerName: payerName.trim() } : {}),
      });
      setCreated(link);
      onCreated();
    } catch (err: any) {
      setError(err?.message || "Failed to create the link");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-700 dark:text-white";

  // Same look as the Pay Now / Continue buttons on the payer page
  const primaryBtn =
    "w-full rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white transition-all duration-200 hover:bg-blue-700 active:scale-95 dark:bg-blue-700 dark:hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-blue-400 disabled:hover:bg-blue-400 disabled:active:scale-100 dark:disabled:bg-blue-900/60";

  const formatMoney = (n: number) =>
    `${n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency}`;

  // Show the panel as soon as a wallet is picked and an amount is typed
  const showFeePanel = !!currency && hasValidAmount;

  const feeSpinner = (
    <span className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400">
      <Spinner className="h-4 w-4" />
      Fetching fee...
    </span>
  );

  const renderFeeValue = () => {
    if (feePending) return feeSpinner;
    if (feeError || fee === null) return "Unavailable";
    return formatMoney(fee);
  };

  const renderReceiveValue = () => {
    if (feePending) return feeSpinner;
    if (feeError || fee === null) return "—";
    return formatMoney(Math.max(amountValue - fee, 0));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900 dark:shadow-2xl"
    >
      {created ? (
        <div>
          <h2 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">
            Payment link created
          </h2>
          <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
            Share this link with the person paying you. You can copy it again
            later from the View button on your payment links list.
          </p>

          <div className="mb-4 flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
            <span className="break-all text-sm text-gray-900 dark:text-white">
              {buildPayUrl(created.token)}
            </span>
            <CopyButton text={buildPayUrl(created.token)} />
          </div>

          <button type="button" onClick={onClose} className={primaryBtn}>
            Done
          </button>
        </div>
      ) : (
        <div>
          <h2 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">
            Create wallet payment link
          </h2>
          <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
            Anyone with the link can pay into your wallet. They don't need an
            account.
          </p>

          {error && (
            <div className="mb-4">
              <Alert variant="error" title="Error" message={error} />
            </div>
          )}

          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Wallet to fund
            </label>
            {loadingWallets ? (
              <Spinner className="h-5 w-5" />
            ) : (
              <div className="flex items-center gap-3">
                <CurrencyFlag
                  src={wallets.find((w) => w.currency === currency)?.flag}
                  alt={currency}
                  className="h-8 w-8"
                />
                <select
                  value={currency}
                  onChange={(e) => {
                    setCurrency(e.target.value);
                    setIso2("");
                    setError("");
                  }}
                  className={`${inputClass} flex-1`}
                >
                  <option value="">Select a wallet</option>
                  {wallets.map((w) => (
                    <option key={w.currency} value={w.currency}>
                      {w.name} ({w.currency})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Only when a currency spans several countries (e.g. XOF) */}
          {needsCountry && supportingCountries.length > 1 && (
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Country
              </label>
              <select
                value={iso2}
                onChange={(e) => setIso2(e.target.value)}
                className={inputClass}
              >
                <option value="">Select a country</option>
                {supportingCountries.map((c: any) => (
                  <option key={`${c.code}-${c.iso2}`} value={c.iso2}>
                    {c.country} (+{c.dialCode})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Amount
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
              placeholder="Enter amount"
              className={inputClass}
            />
          </div>

          {/* Fee breakdown, same logic as Fund Wallet */}
          {showFeePanel && (
            <div className="mb-4 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
              {!resolvedIso2 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {!hasWalletSupportedLoaded
                    ? "Loading currency details..."
                    : needsCountry
                    ? "Select a country to see the fee."
                    : "Could not determine the country for this currency, so the fee cannot be shown."}
                </p>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Fee:
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {renderFeeValue()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Amount to Receive:
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {renderReceiveValue()}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-3 dark:border-gray-700">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Payer pays:
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatMoney(amountValue)}
                    </span>
                  </div>
                  {feeError && (
                    <p className="text-xs text-red-600 dark:text-red-400">
                      Could not load the fee. You can still create the link.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Expires on
            </label>
            <input
              type="datetime-local"
              value={expiresAt}
              min={toLocalInput(new Date())}
              max={toLocalInput(new Date(Date.now() + 30 * 86400 * 1000))}
              onChange={(e) => setExpiresAt(e.target.value)}
              className={inputClass}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Past dates are not allowed. The link can run for up to 30 days.
            </p>
          </div>

          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Description (optional)
            </label>
            <textarea
              value={description}
              maxLength={500}
              rows={3}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this payment for?"
              className={inputClass}
            />
          </div>

          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Payer name (optional)
            </label>
            <input
              value={payerName}
              maxLength={100}
              onChange={(e) => setPayerName(e.target.value)}
              placeholder="Who is this link for?"
              className={inputClass}
            />
          </div>

          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || feePending}
              className={primaryBtn}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Spinner className="h-4 w-4" />
                  Creating...
                </span>
              ) : feePending ? (
                <span className="flex items-center justify-center gap-2">
                  <Spinner className="h-4 w-4" />
                  Fetching fee...
                </span>
              ) : (
                "Create link"
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-lg border border-gray-300 bg-white px-6 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};
